"use client"

import { createContext, useState, useEffect, useContext, useMemo } from "react"
import { getSupabase } from "../lib/supabase"
import { useRouter } from "next/navigation"
import axios from "axios"

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [locale, setLocale] = useState("en")
  const router = useRouter()

  const fetchUser = async (session) => {
    try {
      if (!session) {
        setUser(null)
        return
      }

      const supabaseUser = session.user
      if (!supabaseUser) {
        setUser(null)
        return
      }

      setUser(supabaseUser)
    } catch (error) {
      console.error("Error fetching user:", error)
      setUser(null)
    }
  }

  useEffect(() => {
    const supabase = getSupabase()

    const restoreSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session) {
        await fetchUser(session)
      }

      const { data: listener } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (session) {
            await fetchUser(session)
          } else {
            setUser(null)
          }
        }
      )

      return () => listener.subscription.unsubscribe()
    }

    restoreSession()
  }, [])

  const updateLocale = (newLocale) => {
    setLocale(newLocale)
    if (user) {
      axios
        .post("/api/user/updateSettings", {
          uid: user.uid,
          updates: { locale: newLocale },
        })
        .catch((err) => console.error("Failed to update locale:", err))
    }
  }

  const logout = async () => {
    const supabase = getSupabase()
    await supabase.auth.signOut()
    setUser(null)
    router.push("/")
  }

  const contextValue = useMemo(
    () => ({ user, setUser, locale, updateLocale, logout }),
    [user, locale]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
